'use client';

import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import { DEFAULT_LANGUAGE, normalizeLanguage } from './constants';
import { getCachedAppLanguage } from './language-storage';
import { getLandingLanguage } from './landing-language-storage';
import {
  isPublicLoginRoute,
  isLandingRoute,
} from './public-serbian-routes';

import srCommon from '../../../public/locales/sr/common.json';
import enCommon from '../../../public/locales/en/common.json';
import srNav from '../../../public/locales/sr/nav.json';
import enNav from '../../../public/locales/en/nav.json';
import srErrors from '../../../public/locales/sr/errors.json';
import enErrors from '../../../public/locales/en/errors.json';
import srAuth from '../../../public/locales/sr/auth.json';
import enAuth from '../../../public/locales/en/auth.json';
import srSettings from '../../../public/locales/sr/settings.json';
import enSettings from '../../../public/locales/en/settings.json';
import srShell from '../../../public/locales/sr/shell.json';
import enShell from '../../../public/locales/en/shell.json';
import srDashboard from '../../../public/locales/sr/dashboard.json';
import enDashboard from '../../../public/locales/en/dashboard.json';
import srDepartments from '../../../public/locales/sr/departments.json';
import enDepartments from '../../../public/locales/en/departments.json';
import srUsers from '../../../public/locales/sr/users.json';
import enUsers from '../../../public/locales/en/users.json';
import srAttendance from '../../../public/locales/sr/attendance.json';
import enAttendance from '../../../public/locales/en/attendance.json';
import srDevices from '../../../public/locales/sr/devices.json';
import enDevices from '../../../public/locales/en/devices.json';
import srSync from '../../../public/locales/sr/sync.json';
import enSync from '../../../public/locales/en/sync.json';
import srAssignments from '../../../public/locales/sr/assignments.json';
import enAssignments from '../../../public/locales/en/assignments.json';
import srWorkers from '../../../public/locales/sr/workers.json';
import enWorkers from '../../../public/locales/en/workers.json';
import srTenants from '../../../public/locales/sr/tenants.json';
import enTenants from '../../../public/locales/en/tenants.json';
import srIntegrations from '../../../public/locales/sr/integrations.json';
import enIntegrations from '../../../public/locales/en/integrations.json';
import srProfile from '../../../public/locales/sr/profile.json';
import enProfile from '../../../public/locales/en/profile.json';
import srLegal from '../../../public/locales/sr/legal.json';
import enLegal from '../../../public/locales/en/legal.json';
import srLogs from '../../../public/locales/sr/logs.json';
import enLogs from '../../../public/locales/en/logs.json';
import srSubscription from '../../../public/locales/sr/subscription.json';
import enSubscription from '../../../public/locales/en/subscription.json';
import srModals from '../../../public/locales/sr/modals.json';
import enModals from '../../../public/locales/en/modals.json';
import srLanding from '../../../public/locales/sr/landing.json';
import enLanding from '../../../public/locales/en/landing.json';
import srQueue from '../../../public/locales/sr/queue.json';
import enQueue from '../../../public/locales/en/queue.json';
import srShifts from '../../../public/locales/sr/shifts.json';
import enShifts from '../../../public/locales/en/shifts.json';
import srReports from '../../../public/locales/sr/reports.json';
import enReports from '../../../public/locales/en/reports.json';
import srHelp from '../../../public/locales/sr/help.json';
import enHelp from '../../../public/locales/en/help.json';
import srFeedback from '../../../public/locales/sr/feedback.json';
import enFeedback from '../../../public/locales/en/feedback.json';
import srPlatform from '../../../public/locales/sr/platform.json';
import enPlatform from '../../../public/locales/en/platform.json';
import srAssist from '../../../public/locales/sr/assist.json';
import enAssist from '../../../public/locales/en/assist.json';
import srTimeOff from '../../../public/locales/sr/time-off.json';
import enTimeOff from '../../../public/locales/en/time-off.json';
import srSimulator from '../../../public/locales/sr/simulator.json';
import enSimulator from '../../../public/locales/en/simulator.json';
import srFleet from '../../../public/locales/sr/fleet.json';
import enFleet from '../../../public/locales/en/fleet.json';

const resources = {
  sr: {
    common: srCommon,
    nav: srNav,
    errors: srErrors,
    auth: srAuth,
    settings: srSettings,
    shell: srShell,
    dashboard: srDashboard,
    departments: srDepartments,
    users: srUsers,
    attendance: srAttendance,
    devices: srDevices,
    sync: srSync,
    assignments: srAssignments,
    workers: srWorkers,
    tenants: srTenants,
    integrations: srIntegrations,
    profile: srProfile,
    legal: srLegal,
    logs: srLogs,
    subscription: srSubscription,
    modals: srModals,
    landing: srLanding,
    queue: srQueue,
    shifts: srShifts,
    reports: srReports,
    help: srHelp,
    feedback: srFeedback,
    platform: srPlatform,
    assist: srAssist,
    'time-off': srTimeOff,
    simulator: srSimulator,
    fleet: srFleet,
  },
  en: {
    common: enCommon,
    nav: enNav,
    errors: enErrors,
    auth: enAuth,
    settings: enSettings,
    shell: enShell,
    dashboard: enDashboard,
    departments: enDepartments,
    users: enUsers,
    attendance: enAttendance,
    devices: enDevices,
    sync: enSync,
    assignments: enAssignments,
    workers: enWorkers,
    tenants: enTenants,
    integrations: enIntegrations,
    profile: enProfile,
    legal: enLegal,
    logs: enLogs,
    subscription: enSubscription,
    modals: enModals,
    landing: enLanding,
    queue: enQueue,
    shifts: enShifts,
    reports: enReports,
    help: enHelp,
    feedback: enFeedback,
    platform: enPlatform,
    assist: enAssist,
    'time-off': enTimeOff,
    simulator: enSimulator,
    fleet: enFleet,
  },
} as const;

let initialized = false;

/** Keep in-memory bundles in sync when locale JSON is hot-reloaded in dev. */
function syncResourceBundles() {
  (['sr', 'en'] as const).forEach((lang) => {
    for (const [ns, bundle] of Object.entries(resources[lang])) {
      i18n.addResourceBundle(lang, ns, bundle, true, true);
    }
  });
}

export function initI18n(initialLanguage?: string) {
  const lng = normalizeLanguage(initialLanguage);
  if (initialized) {
    syncResourceBundles();
    if (normalizeLanguage(i18n.language) !== lng) {
      i18n.language = lng;
      i18n.emit('languageChanged', lng);
    }
    return i18n;
  }

  void i18n.use(initReactI18next).init({
    resources,
    lng,
    fallbackLng: 'en',
    defaultNS: 'common',
    ns: Object.keys(resources.sr),
    interpolation: { escapeValue: false },
    react: { useSuspense: false },
  });

  initialized = true;
  return i18n;
}

// Before first paint on the client: landing/login use landing language, else app cache.
if (typeof window !== 'undefined') {
  const path = window.location.pathname;
  if (isLandingRoute(path)) {
    initI18n(getLandingLanguage());
  } else if (isPublicLoginRoute(path)) {
    initI18n(getLandingLanguage());
  } else {
    initI18n(getCachedAppLanguage());
  }
}

export default i18n;
